import React, { FC, SyntheticEvent, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { PageContainer } from "@ui/index";

import FAQBGDesktop from "@assets/webp/home-page/faq-desktop.webp";
import FAQBGMobile from "@assets/webp/home-page/faq-mobile.webp";

import { useAuth } from "@hooks/auth";
import { useIsDesktop } from "@hooks/layout";

import StyledAccordion from "@components/Accordion";
import { QuestionFlowDialog } from "@components/QuestionFlowDialog";
import { HandleOpenDialog } from "@layouts/SecondRootLayout";
import { ERoutes } from "@utils/constants";
import styled from "styled-components";

import { FREE_BACKGROUND_MUSIC, FREE_USE_TERMS, LICENSING, LICENSING_FORM, MUSIC_CATALOG, SUBSCRIPTION } from "./constants";

/* ─── Music Catalog tab categories ─── */

const CATALOG_TABS = [
  { key: "all", label: "All" },
  { key: "free-use", label: "Free Use & Copyright" },
  { key: "subscription", label: "Subscription" },
  { key: "usage", label: "Music Usage" },
  { key: "downloads", label: "Downloads" },
  { key: "trust", label: "Safety" },
  { key: "licensing", label: "Licensing" },
] as const;

type CatalogTabKey = (typeof CATALOG_TABS)[number]["key"];

// Maps each MUSIC_CATALOG index to a tab category
const CATALOG_INDEX_TO_TAB: Record<number, CatalogTabKey> = {
  0: "free-use",    // Is your work in the public domain & royalty-free?
  1: "free-use",    // Can I use Audio Sanctum's music for monetized videos?
  2: "free-use",    // I Received A Copyright Claim or Strike
  3: "free-use",    // Do I always have to credit Audio Sanctum?
  4: "free-use",    // Can I translate the credit text?
  5: "usage",       // Do you use AI to create your music?
  6: "usage",       // Can I modify the music for my projects?
  7: "usage",       // Am I allowed to sample your music?
  8: "usage",       // Am I allowed to sing or rap on top of your music?
  9: "downloads",   // Do you offer wav files for download?
  10: "licensing",  // Do you also offer exclusive custom music creation?
  11: "downloads",  // Do you offer extended track versions?
  // 12 = "I used up my download limit" — intentionally UNMAPPED so it's hidden
  // from /music (kept in the array to preserve the other pages' index refs).
  13: "free-use",   // Am I eligible to use Audio Sanctum's music for free?
  14: "free-use",   // What do I need to do to use your music for free?
  15: "free-use",   // Is there a limit on how many tracks I can use for free?
  16: "usage",      // How can I further support you and give back?
  17: "licensing",  // What types of licenses does Audio Sanctum offer?
  18: "licensing",  // What is the process for licensing music from you?
  19: "licensing",  // What does a license cost?
  20: "subscription", // What is the Audio Sanctum subscription?
  21: "subscription", // What's included in a subscription?
  22: "subscription", // What is a copyright claim, and how does whitelisting work?
  23: "subscription", // How many claims do I get, and what if I run out?
  24: "subscription", // Do I still need credit text if I subscribe?
  25: "subscription", // Can I use the subscription on livestreams / a music channel?
  26: "subscription", // Monthly or annual — what's the difference?
  27: "subscription", // Can I cancel anytime?
  28: "subscription", // Subscription, single license, or free use — which?
  29: "trust",        // Is it safe to use Audio Sanctum?
  30: "trust",        // Do you need access to my YouTube channel?
  31: "trust",        // Do you get a share of my YouTube revenue?
  32: "trust",        // Is my payment information secure?
  33: "trust",        // What do you do with my personal data?
  34: "trust",        // Will using your music get my channel a copyright strike?
};

/* ─── Free Use tab categories ─── */

const FREE_USE_TABS = [
  { key: "all", label: "All" },
  { key: "eligibility", label: "Eligibility" },
  { key: "getting-started", label: "Getting Started" },
  { key: "credit", label: "Credit & Claims" },
  { key: "rules", label: "Usage Rules" },
  { key: "licensing", label: "Licensing" },
] as const;

type FreeUseTabKey = (typeof FREE_USE_TABS)[number]["key"];

const FREE_USE_INDEX_TO_TAB: Record<number, FreeUseTabKey> = {
  0: "eligibility",     // Am I eligible to use Audio Sanctum's music for free?
  1: "eligibility",     // Can I use Audio Sanctum's music for monetized videos?
  2: "credit",          // Do I always have to credit Audio Sanctum?
  3: "rules",           // Can I modify the music for my projects?
  4: "rules",           // Am I allowed to sample your music?
  5: "rules",           // Am I allowed to sing or rap on top of your music?
  6: "credit",          // Can I translate the credit text?
  7: "getting-started", // Do I need to register to access the free background music?
  8: "getting-started", // What do I need to do to use your music for free?
  9: "credit",          // I Received A Copyright Claim or Strike
  10: "licensing",      // I need a license, where can I get one?
  11: "eligibility",    // Is there a limit on how many tracks I can use for free?
  12: "getting-started",// How can I download the free background music tracks?
  13: "licensing",      // How can I support you further?
};

/* ─── Pill tab styles ─── */

/* Swipable single-pill row: no surrounding box. Centres when the pills fit,
   scrolls horizontally when they don't (safe center keeps the first pill
   reachable). Scrollbar hidden — it's a touch-swipe affordance. */
const TabsScroller = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
  padding: 4px 2px;
  justify-content: center;

  /* Desktop (default above): the pills WRAP into centered rows — a horizontal
     swipe-scroll can't be driven with a mouse, so everything is visible and
     clickable at rest.

     Mobile/tablet (≤900px): a single non-wrapping swipable row that breaks out
     to the full viewport width so the pills can scroll past the edges, resting
     with the FIRST pill aligned to the section's content padding
     (calc(50vw - 50%) == the ContentContainer padding-inline). The
     PageContainer's overflow:hidden clips the 100vw so the page itself doesn't
     gain a horizontal scrollbar; the touch swipe is the affordance. */
  @media (max-width: 900px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    width: 100vw;
    max-width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding-left: calc(50vw - 50%);
    padding-right: 0;
    justify-content: flex-start;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const TabPill = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  height: 38px;
  padding: 0 18px;
  border-radius: 20px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.18)" : "rgba(255, 255, 255, 0.05)")};
  border: 1px solid ${({ $active }) => ($active ? "rgba(255, 210, 155, 0.65)" : "rgba(186, 144, 92, 0.25)")};
  color: ${({ $active }) => ($active ? "#fbf4ec" : "rgba(251, 244, 236, 0.65)")};
  box-shadow: ${({ $active }) => ($active ? "0 0 16px rgba(186, 144, 92, 0.28)" : "none")};

  &:hover {
    color: #fbf4ec;
    border-color: ${({ $active }) => ($active ? "rgba(255, 210, 155, 0.65)" : "rgba(186, 144, 92, 0.45)")};
  }
`;

/* ─── Component ─── */

type TFAQ = {
  isMusicCatalog?: boolean;
  isFreeUseTerms?: boolean;
  isFreeBackgroundMusic?: boolean;
  isLicensing?: boolean;
  isLicensingForm?: boolean;
  isSubscription?: boolean;
  headerText: string;
  headerDescription: string;
  id?: string;
  widthDivider?: boolean;
};

export const FAQ: FC<TFAQ> = ({
  isMusicCatalog,
  isFreeBackgroundMusic,
  isFreeUseTerms,
  isLicensingForm,
  isLicensing,
  isSubscription,
  headerText,
  headerDescription,
  id,
  widthDivider = false,
}) => {
  const isDesktop = useIsDesktop();
  // The FAQ section shrinks to fit the selected category at EVERY breakpoint
  // (phone, tablet, desktop) instead of locking to a panel sized to the tallest
  // ("All") list — so short categories no longer leave a big empty gap below.
  // The background image stays put regardless because it's pinned to 100vh on
  // the BackgroundLayer + clipped (see the PageContainer style), so a shorter
  // list just reveals less of the same stationary image.
  // True desktop = the 1150 header swap; phone + tablet (<1150) get the short
  // header rule. (isDesktop above is the md=900 break, too low for this.)
  const isDesktopLayout = useMediaQuery("(min-width:1150px)");

  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState<string | false>(false);
  const [openQuestionDialog, setOpenQuestionDialog] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  // Desktop pill-row balancing: index before which to force a line break so the
  // wrapped last row never holds a single orphaned pill (null = no forced break).
  const [breakIndex, setBreakIndex] = useState<number | null>(null);
  const tabsScrollerRef = useRef<HTMLDivElement>(null);
  const tabPillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // Smooth list-height animation on category change (FLIP): wrapper clips +
  // transitions height, content is measured, fromHeightRef holds the pre-switch
  // height snapshotted in handleTabChange.
  const heightWrapRef = useRef<HTMLDivElement>(null);
  const listContentRef = useRef<HTMLDivElement>(null);
  const fromHeightRef = useRef<number | null>(null);
  const handleOpenDialog = useContext(HandleOpenDialog);

  const hasTabs = isMusicCatalog || isFreeUseTerms;

  // Keep the active pill in view without disturbing the resting position: only
  // scroll when the active pill is actually off-screen (off the left → align it
  // to the content padding; off the right → bring it fully in). The first pill
  // therefore stays put at the section's left padding at rest.
  useEffect(() => {
    const scroller = tabsScrollerRef.current;
    const pill = tabPillRefs.current.get(activeTab);
    if (!scroller || !pill) return;
    const sc = scroller.getBoundingClientRect();
    const p = pill.getBoundingClientRect();
    const padLeft = parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
    if (p.left < sc.left + padLeft) {
      scroller.scrollBy({ left: p.left - sc.left - padLeft, behavior: "smooth" });
    } else if (p.right > sc.right) {
      scroller.scrollBy({ left: p.right - sc.right + 8, behavior: "smooth" });
    }
  }, [activeTab]);

  // Desktop pill-row balancing. The pills wrap centered; with this many pills the
  // last row can end up with a single orphaned pill, which reads as unbalanced.
  // Simulate the natural left-to-right wrap (justify-content only changes
  // positioning, not which pills wrap) and, if the last row would hold exactly
  // one pill, force the last TWO onto their own row (a full-width break element
  // before the second-to-last pill). Mobile (≤900, the swipe row) is left alone.
  const recomputeRowBalance = useCallback(() => {
    if (!isDesktop || !hasTabs) { setBreakIndex(null); return; }
    const scroller = tabsScrollerRef.current;
    if (!scroller) return;
    const tabsList = isMusicCatalog ? CATALOG_TABS : FREE_USE_TABS;
    const widths = tabsList.map((t) => tabPillRefs.current.get(t.key)?.offsetWidth ?? 0);
    if (widths.some((w) => w === 0)) return; // not measured yet
    const cs = getComputedStyle(scroller);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const avail = scroller.clientWidth - padX;
    const GAP = 8;
    const rows: number[][] = [];
    let cur: number[] = [];
    let x = 0;
    widths.forEach((w, i) => {
      const needed = cur.length === 0 ? w : x + GAP + w;
      if (cur.length > 0 && needed > avail) { rows.push(cur); cur = [i]; x = w; }
      else { cur.push(i); x = needed; }
    });
    if (cur.length) rows.push(cur);
    if (rows.length >= 2 && rows[rows.length - 1].length === 1) setBreakIndex(widths.length - 2);
    else setBreakIndex(null);
  }, [isDesktop, hasTabs, isMusicCatalog]);

  useLayoutEffect(() => { recomputeRowBalance(); }, [recomputeRowBalance]);
  useEffect(() => {
    const onResize = () => recomputeRowBalance();
    window.addEventListener("resize", onResize);
    // Pill widths shift once the Raleway webfont loads — re-balance then too.
    (document as { fonts?: { ready?: Promise<unknown> } }).fonts?.ready?.then?.(() => recomputeRowBalance());
    return () => window.removeEventListener("resize", onResize);
  }, [recomputeRowBalance]);

  // Animate the list height when the category changes: handleTabChange snapshots
  // the old height, this measures the new one, transitions between them, then
  // releases to `auto` so accordion expand/collapse stays unconstrained (no clip).
  useLayoutEffect(() => {
    const wrap = heightWrapRef.current;
    const content = listContentRef.current;
    if (!wrap || !content || fromHeightRef.current == null) return;
    const from = fromHeightRef.current;
    fromHeightRef.current = null;
    const to = content.offsetHeight;
    if (from === to) return;
    wrap.style.transition = "none";
    wrap.style.height = `${from}px`;
    void wrap.offsetHeight; // force reflow so the start height sticks
    wrap.style.transition = "height 0.38s cubic-bezier(0.4, 0, 0.2, 1)";
    wrap.style.height = `${to}px`;
    let done = false;
    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName !== "height") return;
      done = true;
      wrap.style.transition = "none";
      wrap.style.height = "auto";
    };
    wrap.addEventListener("transitionend", onEnd);
    return () => {
      wrap.removeEventListener("transitionend", onEnd);
      if (!done) {
        wrap.style.transition = "none";
        wrap.style.height = "auto";
      }
    };
  }, [activeTab]);

  const handleChange = (panel: string) => (_event: SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleOpenQuestionFlow = () => {
    setOpenQuestionDialog(true);
  };

  const handleOpenLoginDialog = (): void => {
    if (isLoggedIn) {
      return navigate(ERoutes.Catalog);
    }
    handleOpenDialog(false);
  };

  const handleTabChange = (key: string) => {
    if (key === activeTab) return;
    // Snapshot the current list height so the layout effect can animate from it.
    if (heightWrapRef.current) fromHeightRef.current = heightWrapRef.current.offsetHeight;
    setActiveTab(key);
    setExpanded(false);
  };

  const getFAQs = useCallback(() => {
    switch (true) {
      case isMusicCatalog:
        return MUSIC_CATALOG(handleOpenQuestionFlow, handleOpenLoginDialog);
      case isFreeUseTerms:
        return FREE_USE_TERMS(handleOpenQuestionFlow, handleOpenLoginDialog);
      case isFreeBackgroundMusic:
        return FREE_BACKGROUND_MUSIC(handleOpenQuestionFlow, handleOpenLoginDialog);
      case isLicensingForm:
        return LICENSING_FORM(handleOpenQuestionFlow, handleOpenLoginDialog);
      case isLicensing:
        return LICENSING(handleOpenQuestionFlow, handleOpenLoginDialog);
      case isSubscription:
        return SUBSCRIPTION(handleOpenQuestionFlow, handleOpenLoginDialog);
      default:
        return [];
    }
  }, [isMusicCatalog, isFreeUseTerms, isFreeBackgroundMusic, isLicensingForm, isLicensing, isSubscription]);

  const allFaqs = getFAQs();
  const filteredFaqs = activeTab !== "all" && hasTabs
    ? allFaqs.filter((_, i) => {
        if (isMusicCatalog) return CATALOG_INDEX_TO_TAB[i] === activeTab;
        if (isFreeUseTerms) return FREE_USE_INDEX_TO_TAB[i] === activeTab;
        return true;
      })
    // "all" tab: for the catalog set, drop any question with no tab mapping —
    // that's how a question is hidden from /music without renumbering the
    // index-based references the other FAQ pages rely on.
    : isMusicCatalog
      ? allFaqs.filter((_, i) => CATALOG_INDEX_TO_TAB[i] !== undefined)
      : allFaqs;

  return (
    <>
      <PageContainer
        id={id}
        backgroundImage={isDesktop ? FAQBGDesktop : FAQBGMobile}
        withDivider={widthDivider}
        style={{
          // Pin the BackgroundLayer to a fixed 100vh height and clip anything
          // that overflows the Container's visible box — at EVERY resolution
          // (phone, tablet, desktop). The default (`height: 100% of container,
          // capped at 100vh`) makes the layer change size whenever the
          // container grows (e.g. expanding an accordion), and
          // `background-size: cover` then re-scales the image, producing the
          // visible "background moves/zooms" effect. Locking the layer to 100vh
          // + clipping with overflow:hidden keeps the image stationary; if the
          // section grows past 100vh, the area below shows the container's solid
          // background (masked to a soft fade). The container itself still
          // shrinks-to-fit on phone via PageContainer's own min-height, so a
          // short category doesn't leave a tall gap. PageContainer routes
          // `height`/`maxHeight` to the BackgroundLayer and `overflow` to the
          // Container, so each part gets the right rule.
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
          maskImage: "linear-gradient(to bottom, black 80%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 80%, transparent 100%)",
        }}
      >
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: isDesktop ? "center" : "flex-start",
          gap: isDesktop ? "24px" : "12px",
          width: "100%",
        }}>
          {/* Header */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "24px",
            width: isDesktop ? "50%" : "100%",
            textAlign: "center",
          }}>
            <h2 style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: isDesktop ? "2rem" : "1.5rem",
              fontWeight: 400,
              color: "#ffffff",
              margin: 0,
              lineHeight: 1.2,
            }}>
              {headerText}
            </h2>
            <div style={{ width: isDesktopLayout ? "200px" : "80px", height: "1px", background: "linear-gradient(93deg, #ba905c 0.82%, #ffd29b 33.73%, #ffe0b1 47.65%, #ffd29b 61.53%, #ba905c 99.48%)" }} />
            <p style={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#e0d2c1",
              margin: 0,
              lineHeight: 1.6,
            }}>
              {headerDescription}
            </p>
          </div>

          {/* Tabs + Accordion list */}
          <div style={{
            width: isDesktop ? "65%" : "100%",
            display: "flex",
            flexDirection: "column",
            gap: "28px",
          }}>
            {hasTabs && (
              <TabsScroller ref={tabsScrollerRef}>
                {(isMusicCatalog ? CATALOG_TABS : FREE_USE_TABS).map((tab, idx) => (
                  <React.Fragment key={tab.key}>
                    {breakIndex === idx && (
                      <div aria-hidden style={{ flexBasis: "100%", height: 0, margin: "-4px 0" }} />
                    )}
                    <TabPill
                      ref={(el) => {
                        if (el) tabPillRefs.current.set(tab.key, el);
                        else tabPillRefs.current.delete(tab.key);
                      }}
                      $active={activeTab === tab.key}
                      onClick={() => handleTabChange(tab.key)}
                    >
                      {tab.label}
                    </TabPill>
                  </React.Fragment>
                ))}
              </TabsScroller>
            )}

          <div ref={heightWrapRef} style={{ overflow: "hidden" }}>
            <div
              ref={listContentRef}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isDesktop ? "12px" : "10px",
              }}
            >
              {filteredFaqs.map(({ description, title, needsReview }, i) => (
                <StyledAccordion
                  key={`${activeTab}-${i}`}
                  title={title}
                  description={description}
                  needsReview={needsReview}
                  panel={`panel_${i}`}
                  expanded={expanded}
                  handleChange={handleChange}
                />
              ))}
            </div>
          </div>
          </div>
        </div>
      </PageContainer>
      {openQuestionDialog && (
        <QuestionFlowDialog open={openQuestionDialog} onClose={() => setOpenQuestionDialog(false)} />
      )}
    </>
  );
};
